import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Palette, Upload, Save, ExternalLink, Info } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { getBranding, updateBranding, uploadBrandLogo } from '../../services/taskApi';

const DEFAULT = { company_name: '', tagline: '', logo_url: '', primary_color: '#1a2856', accent_color: '#c89b3c', address: '', phone: '', email: '', website: '', sender_display_name: '', social: { linkedin: '', instagram: '', youtube: '', facebook: '', whatsapp: '' } };

const FlowBranding = () => {
  const { isDark } = useOutletContext();
  const [b, setB] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const cardCls = isDark ? 'bg-[#0f1729] border-white/10' : 'bg-white border-gray-200';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : '';

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getBranding();
      setB({
        ...DEFAULT,
        ...r.data,
        social: { ...DEFAULT.social, ...(r.data?.social || {}) },
        social_icons: { ...DEFAULT.social_icons, ...(r.data?.social_icons || {}) },
      });
    } catch { toast.error('Failed to load branding'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    setSaving(true);
    try { await updateBranding(b); toast.success('Branding saved — applies to ALL future emails'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { const r = await uploadBrandLogo(f); setB(prev => ({ ...prev, logo_url: r.data.url })); toast.success('Logo uploaded'); }
    catch (err) { toast.error('Upload failed'); }
  };

  const uploadSocialIcon = async (platform, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) { toast.error('Icon must be under 500 KB'); return; }
    try {
      const r = await uploadBrandSocialIcon(f);
      setB(prev => ({ ...prev, social_icons: { ...prev.social_icons, [platform]: r.data.url } }));
      toast.success(`${platform} icon uploaded — click Save to apply`);
    } catch { toast.error('Icon upload failed'); }
  };

  const clearSocialIcon = (platform) => {
    setB(prev => ({ ...prev, social_icons: { ...prev.social_icons, [platform]: '' } }));
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  const socials = [
    { k: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/...' },
    { k: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...' },
    { k: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@...' },
    { k: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/...' },
    { k: 'whatsapp', label: 'WhatsApp', placeholder: 'https://wa.me/91XXXXXXXXXX' },
  ];

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Palette size={20} /> Email Branding (Master)
          </h1>
          <p className={`text-xs mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            One config controls the banner + footer of <b>every</b> outbound email — Task, Quotation, Flow Management & Marketing.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 h-9 text-xs" data-testid="branding-save-btn">
          <Save size={14} className="mr-1.5" /> {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <Card className={cardCls}>
        <CardContent className="p-4 flex items-start gap-3">
          <Info size={16} className="text-blue-400 mt-0.5 shrink-0" />
          <div className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
            To change the "From" address (e.g. <code>connect@namohtoolings.in</code>), update the SMTP credentials in <b>CMS Admin → Notification Settings</b> with that mailbox's username + app password. The "Sender Display Name" below controls only the visible label.
          </div>
        </CardContent>
      </Card>

      <Card className={cardCls}>
        <CardContent className="p-4 space-y-3">
          <h2 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Brand identity</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Company Name</Label>
              <Input className={`mt-1 ${inputCls}`} value={b.company_name} onChange={e => setB({ ...b, company_name: e.target.value })} data-testid="brand-company" />
            </div>
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Tagline</Label>
              <Input className={`mt-1 ${inputCls}`} value={b.tagline} onChange={e => setB({ ...b, tagline: e.target.value })} data-testid="brand-tagline" />
            </div>
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Primary Color (header/footer)</Label>
              <div className="flex gap-2 mt-1">
                <Input className={inputCls} value={b.primary_color} onChange={e => setB({ ...b, primary_color: e.target.value })} />
                <input type="color" value={b.primary_color} onChange={e => setB({ ...b, primary_color: e.target.value })} className="h-9 w-12 rounded cursor-pointer" />
              </div>
            </div>
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Accent Color (tagline)</Label>
              <div className="flex gap-2 mt-1">
                <Input className={inputCls} value={b.accent_color} onChange={e => setB({ ...b, accent_color: e.target.value })} />
                <input type="color" value={b.accent_color} onChange={e => setB({ ...b, accent_color: e.target.value })} className="h-9 w-12 rounded cursor-pointer" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className={isDark ? 'text-white/60' : ''}>Logo URL</Label>
              <div className="flex gap-2 mt-1">
                <Input className={`flex-1 ${inputCls}`} value={b.logo_url} onChange={e => setB({ ...b, logo_url: e.target.value })} placeholder="https://..." data-testid="brand-logo-url" />
                <label className={`inline-flex items-center gap-1 px-3 h-9 rounded-lg text-xs font-medium cursor-pointer ${isDark ? 'border border-white/20 text-white/70 hover:bg-white/5' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  <Upload size={12} /> Upload
                  <input type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden" onChange={uploadLogo} />
                </label>
              </div>
              <p className={`mt-1 text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Recommended: transparent PNG / SVG, 220×80 px (display height ~44px)</p>
              {b.logo_url && <img src={b.logo_url} alt="logo" className="mt-2 max-h-16 rounded border bg-slate-100" />}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className={cardCls}>
        <CardContent className="p-4 space-y-3">
          <h2 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Sender & footer details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Sender Display Name</Label>
              <Input className={`mt-1 ${inputCls}`} value={b.sender_display_name} onChange={e => setB({ ...b, sender_display_name: e.target.value })} placeholder="Namoh Toolings" data-testid="brand-sender-name" />
            </div>
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Contact Email (shown in footer)</Label>
              <Input className={`mt-1 ${inputCls}`} value={b.email} onChange={e => setB({ ...b, email: e.target.value })} placeholder="connect@namohtoolings.in" />
            </div>
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Phone</Label>
              <Input className={`mt-1 ${inputCls}`} value={b.phone} onChange={e => setB({ ...b, phone: e.target.value })} placeholder="+91-..." />
            </div>
            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Website</Label>
              <Input className={`mt-1 ${inputCls}`} value={b.website} onChange={e => setB({ ...b, website: e.target.value })} placeholder="https://namohtoolings.in" />
            </div>
            <div className="sm:col-span-2">
              <Label className={isDark ? 'text-white/60' : ''}>Address</Label>
              <Input className={`mt-1 ${inputCls}`} value={b.address} onChange={e => setB({ ...b, address: e.target.value })} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className={cardCls}>
        <CardContent className="p-4 space-y-3">
          <h2 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Social media (footer icons)</h2>
          <p className={`text-[11px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
            Leave URL blank to hide that platform. Upload a <b>custom white icon</b> (PNG/SVG, ~64×64 px, transparent bg) per platform if the default isn't loading in your emails.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {socials.map(s => {
              const customIcon = b.social_icons?.[s.k] || '';
              return (
                <div key={s.k} className={`p-3 rounded-lg border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                  <Label className={`text-xs font-semibold ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{s.label}</Label>
                  <Input className={`mt-1 ${inputCls}`} value={b.social?.[s.k] || ''} placeholder={s.placeholder}
                    onChange={e => setB({ ...b, social: { ...b.social, [s.k]: e.target.value } })} data-testid={`brand-social-${s.k}`} />
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <div className={`flex items-center justify-center h-9 w-9 rounded ${isDark ? 'bg-[#1a2856]' : 'bg-[#1a2856]'}`}>
                      <img
                        src={customIcon || `https://cdn.simpleicons.org/${s.k}/ffffff`}
                        alt={s.label}
                        className="w-5 h-5 object-contain"
                        onError={(e) => { e.currentTarget.style.opacity = '0.2'; }}
                        data-testid={`brand-social-icon-preview-${s.k}`}
                      />
                    </div>
                    <label className={`inline-flex items-center gap-1 px-2.5 h-8 rounded text-[11px] font-medium cursor-pointer ${isDark ? 'border border-white/20 text-white/70 hover:bg-white/5' : 'border border-gray-200 text-gray-600 hover:bg-white'}`}>
                      <Upload size={11} /> {customIcon ? 'Replace icon' : 'Upload icon'}
                      <input type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden"
                        onChange={(e) => uploadSocialIcon(s.k, e)} data-testid={`brand-social-icon-upload-${s.k}`} />
                    </label>
                    {customIcon && (
                      <button type="button" onClick={() => clearSocialIcon(s.k)}
                        className={`text-[11px] underline ${isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-500 hover:text-gray-800'}`}
                        data-testid={`brand-social-icon-reset-${s.k}`}>
                        Reset to default
                      </button>
                    )}
                  </div>
                  {customIcon && (
                    <p className={`mt-1 text-[10px] truncate ${isDark ? 'text-white/40' : 'text-gray-500'}`} title={customIcon}>
                      Using custom icon: <span className="font-mono">{customIcon.split('/').pop()}</span>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className={cardCls}>
        <CardContent className="p-4">
          <h2 className={`text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Live preview</h2>
          <div className="overflow-hidden rounded-lg border" style={{ background: '#f3f4f8' }}>
            <div style={{ background: b.primary_color, padding: '18px 28px', display: 'flex', alignItems: 'center', gap: 14 }}>
              {b.logo_url && <img src={b.logo_url} alt="" style={{ height: 44, width: 'auto', maxWidth: 80 }} />}
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, letterSpacing: 1.5 }}>{b.company_name || 'COMPANY NAME'}</div>
                {b.tagline && <div style={{ color: b.accent_color, fontSize: 10, letterSpacing: 2, marginTop: 3, fontWeight: 600 }}>{b.tagline}</div>}
              </div>
            </div>
            <div style={{ background: '#fff', padding: 24, color: '#333', fontSize: 13, fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Inter,Arial,sans-serif' }}>
              <p>Sample content area — every Task / Quotation / Flow Management / Marketing email will appear here, wrapped by this header &amp; footer.</p>
              <p><a href="#" style={{ display: 'inline-block', padding: '10px 24px', background: b.primary_color, color: '#fff', textDecoration: 'none', borderRadius: 6, fontWeight: 600 }}>Action button</a></p>
            </div>
            <div style={{ background: b.primary_color, padding: 18, textAlign: 'center', color: '#cfd5e0', fontSize: 12 }}>
              <div style={{ marginBottom: 10 }}>
                {Object.entries(b.social || {}).map(([k, v]) => v ? (
                  <a key={k} href={v} style={{ display: 'inline-block', margin: '0 6px', textDecoration: 'none' }}>
                    <img
                      src={b.social_icons?.[k] || `https://cdn.simpleicons.org/${k}/ffffff`}
                      alt={k}
                      style={{ width: 22, height: 22, display: 'block', border: 0 }}
                    />
                  </a>
                ) : null)}
              </div>
              <div style={{ color: '#fff', fontWeight: 600 }}>{b.company_name}</div>
              {b.address && <div>{b.address}</div>}
              <div>{[b.phone, b.email, b.website].filter(Boolean).join(' · ')}</div>
            </div>
          </div>
          <a href="https://www.litmus.com/" target="_blank" rel="noreferrer" className={`mt-2 inline-flex items-center gap-1 text-[10px] ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
            <ExternalLink size={10} /> Test in real mail clients
          </a>
        </CardContent>
      </Card>
    </div>
  );
};

export default FlowBranding;
